/**
 * ChipRow.js — Row of chips with inline expansion
 *
 * Renders chips horizontally with flex-wrap. Only ONE chip can be
 * expanded at a time. When a chip is clicked, its expansion panel
 * appears BELOW the row.
 */

import { useState } from 'react';
import ConditionChip from './ConditionChip';
import CostChip from './CostChip';
import EffectChip from './EffectChip';
import ChipExpansionPanel from './ChipExpansionPanel';

const CHIP_COMPONENTS = {
  condition: ConditionChip,
  cost: CostChip,
  effect: EffectChip,
};

const ChipRow = ({ items, chipType, colorScheme, onUpdate, onRemove }) => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const ChipComponent = CHIP_COMPONENTS[chipType];
  if (!ChipComponent) return null;

  const safeItems = items || [];

  const handleToggleExpand = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const handleUpdate = (index, updated) => {
    onUpdate(index, updated);
  };

  const handleRemove = (index) => {
    // If removing the expanded chip, collapse it
    if (expandedIndex === index) setExpandedIndex(null);
    else if (expandedIndex !== null && index < expandedIndex) {
      setExpandedIndex(expandedIndex - 1);
    }
    onRemove(index);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {safeItems.map((item, index) => (
          <ChipComponent
            key={index}
            item={item}
            index={index}
            isExpanded={expandedIndex === index}
            onToggleExpand={handleToggleExpand}
            onRemove={handleRemove}
            colorScheme={colorScheme}
          />
        ))}
      </div>
      {expandedIndex !== null && expandedIndex < safeItems.length && (
        <ChipExpansionPanel
          item={safeItems[expandedIndex]}
          index={expandedIndex}
          chipType={chipType}
          colorScheme={colorScheme}
          onUpdate={handleUpdate}
          onDone={() => setExpandedIndex(null)}
        />
      )}
    </div>
  );
};

export default ChipRow;
